import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { switchWorkspace, rememberWorkspace, forgetWorkspace } from '../lib/workspaceBoot';

const API = process.env.REACT_APP_API_URL || '';

/**
 * Workspace picker. Shown once after sign-in to people who work in more than
 * one workspace; later sign-ins reopen the remembered choice. Reachable any
 * time from the header switcher. Styled as the sign-in page it follows.
 */
export default function Workspaces() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await fetch(`${API}/api/auth/session`, { credentials: 'include' }).then((r) => r.json());
        if (!s.success) { navigate('/login'); return; }
        setMe(s.user);
        const d = await fetch(`${API}/api/auth/workspaces`, { credentials: 'include' }).then((r) => r.json());
        if (!d.success) throw new Error(d.message || 'Could not load workspaces');
        if (d.workspaces.length <= 1) {
          if (d.workspaces[0]) rememberWorkspace(d.workspaces[0].organization_id);
          navigate('/', { replace: true });
          return;
        }
        setData(d);
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [navigate]);

  const open = async (ws) => {
    setBusy(ws.organization_id);
    setError(null);
    if (ws.organization_id === data.current_organization_id) {
      rememberWorkspace(ws.organization_id);
      window.location.href = '/';
      return;
    }
    const r = await switchWorkspace(ws.organization_id);
    if (r.ok) { window.location.href = '/'; return; }
    setBusy(null);
    setError(r.message);
  };

  const signOut = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: 'GET', credentials: 'include' }); } catch { /* ignore */ }
    try { localStorage.removeItem('sessionUser'); } catch { /* ignore */ }
    forgetWorkspace();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#f8fafc]">
      {/* Ambient tonal glow, as on sign-in */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/3 w-[600px] h-[460px] opacity-40"
             style={{ background: 'radial-gradient(ellipse, rgba(59,130,246,0.18) 0%, transparent 70%)', filter: 'blur(70px)' }} />
        <div className="absolute bottom-1/4 right-1/3 w-[520px] h-[380px] opacity-40"
             style={{ background: 'radial-gradient(ellipse, rgba(139,92,246,0.14) 0%, transparent 70%)', filter: 'blur(70px)' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] opacity-30"
             style={{ background: 'radial-gradient(ellipse, rgba(16,185,129,0.08) 0%, transparent 70%)', filter: 'blur(90px)' }} />
      </div>
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage: 'linear-gradient(rgba(15,23,42,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(15,23,42,0.5) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative z-10 w-full max-w-md px-6 py-10">
        <div className="text-center mb-8">
          <svg className="w-14 h-14 mx-auto mb-4" viewBox="0 0 512 512" role="img" aria-label="Precision Compliance Group">
            <defs>
              <linearGradient id="pcgPickerMark" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#F79A3E" />
                <stop offset="1" stopColor="#E5601F" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#pcgPickerMark)" strokeWidth="26" strokeLinecap="round">
              <path d="M148 88 A 150 150 0 0 0 148 216" />
              <path d="M364 88 A 150 150 0 0 1 364 216" />
            </g>
            <circle cx="256" cy="152" r="46" fill="url(#pcgPickerMark)" />
            <path d="M212 232 h88 l40 214 a16 16 0 0 1 -16 20 H188 a16 16 0 0 1 -16 -20 Z" fill="#0E2841" />
          </svg>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Choose where to work</h1>
          <p className="text-slate-500 text-sm mt-1">Each client is kept separate. You can change client at any time from the top of the page.</p>
        </div>

        <div className="bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm shadow-slate-200/50">
          {me && (
            <div className="flex items-center justify-between gap-3 pb-4 mb-4 border-b border-slate-100">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {initials(me.name)}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate">Signed in as {me.name}</div>
                  <div className="text-xs text-slate-500 truncate">{me.email}</div>
                </div>
              </div>
              <button type="button" onClick={signOut} className="text-xs text-slate-500 hover:text-slate-800 whitespace-nowrap">
                Not you? Sign out
              </button>
            </div>
          )}

          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
          )}

          {!data && !error ? (
            <div className="text-slate-500 text-sm py-4 text-center">Loading…</div>
          ) : data ? (
            <div className="grid gap-2.5">
              {data.workspaces.map((ws) => (
                <button
                  key={ws.organization_id}
                  type="button"
                  disabled={!!busy}
                  onClick={() => open(ws)}
                  className="group text-left rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-400 hover:shadow-sm px-4 py-3.5 flex items-center gap-3.5 transition disabled:opacity-60"
                >
                  <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-700 font-bold text-sm flex-shrink-0">
                    {initials(ws.organization_name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-slate-800 truncate">{ws.organization_name}</div>
                    <div className="text-xs text-slate-500">
                      {ws.cms_contract_number ? `Contract ${ws.cms_contract_number} · ` : ''}
                      {ws.is_home ? 'Your organization' : `You are the ${roleLabel(ws.user_type)}`}
                    </div>
                  </div>
                  <div className="text-xs font-medium text-slate-400 group-hover:text-blue-600 transition">
                    {busy === ws.organization_id ? 'Opening…' : 'Open →'}
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">© 2026 Precision Compliance Group</p>
      </div>
    </div>
  );
}

function initials(name) {
  return String(name || '').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '·';
}
function roleLabel(t) {
  return ({
    org_superadmin: 'access administrator',
    compliance_officer: 'compliance officer',
    compliance_analyst: 'compliance analyst',
    department_lead: 'department lead',
    department_user: 'department user',
  })[t] || 'member';
}
